import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, CalendarClock, Receipt, Landmark, Settings as Cog } from "lucide-react";
import { FinOverview } from "@/components/finance/FinOverview";
import { FinBills } from "@/components/finance/FinBills";
import { FinExpenses } from "@/components/finance/FinExpenses";
import { FinTax } from "@/components/finance/FinTax";
import { FinSettings } from "@/components/finance/FinSettings";

export const NAV = [
  { to: "", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "bills", label: "Bills & loans", icon: CalendarClock },
  { to: "expenses", label: "Expenses", icon: Receipt },
  { to: "tax", label: "Tax", icon: Landmark },
  { to: "settings", label: "Settings", icon: Cog },
];

const THEME = {
  "--fin-bg": "#121316", "--fin-bg2": "#181A1F", "--fin-card": "#1E2026", "--fin-card-hover": "#252830", "--fin-line": "rgba(255,255,255,0.08)",
  "--fin-ink": "#F4F1EA", "--fin-ink2": "#C5C2B8", "--fin-muted": "#8C8980", "--fin-warm": "#E4D5BE", "--fin-gold": "#D6C5AD", "--fin-gold-soft": "rgba(214,197,173,0.14)",
  "--fin-ok": "#81B29A", "--fin-ok-soft": "rgba(129,178,154,0.12)", "--fin-warn": "#E6A15C", "--fin-warn-soft": "rgba(230,161,92,0.12)",
  "--fin-accent": "#E07A5F", "--fin-accent-soft": "rgba(224,122,95,0.12)",
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap');
.fin-root{font-family:Manrope,ui-sans-serif,system-ui,sans-serif;color:var(--fin-ink);background:var(--fin-bg);color-scheme:dark}
.fin-root .fin-h{font-family:Outfit,Manrope,sans-serif;letter-spacing:-0.02em}
.fin-root .fin-big{font-family:Outfit,Manrope,sans-serif;font-size:30px;font-weight:700;letter-spacing:-0.02em;font-variant-numeric:tabular-nums}
.fin-root .fin-card{background:var(--fin-card);border:1px solid var(--fin-line);border-radius:20px;transition:border-color .2s ease,background-color .2s ease}
.fin-root .fin-card.hover:hover{background:var(--fin-card-hover);border-color:rgba(255,255,255,.16)}
.fin-root .fin-sub{background:var(--fin-bg2);border:1px solid rgba(255,255,255,.05);border-radius:14px}
.fin-root .fin-muted{color:var(--fin-muted)} .fin-root .fin-ink2{color:var(--fin-ink2)} .fin-root .fin-accent{color:var(--fin-accent)} .fin-root .fin-gold{color:var(--fin-gold)}
.fin-root .fin-label{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--fin-muted);font-weight:600}
.fin-root .fin-input,.fin-root select.fin-input{background:var(--fin-bg2);border:1px solid var(--fin-line);color:var(--fin-ink);border-radius:12px;padding:9px 12px;font-size:14px;outline:none;transition:border-color .2s}
.fin-root .fin-input:focus{border-color:rgba(214,197,173,.5)} .fin-root .fin-input::placeholder{color:var(--fin-muted)}
.fin-root input[type=file]{color:var(--fin-ink2)} .fin-root input[type=checkbox]{accent-color:var(--fin-gold)}
.fin-root .fin-btn{background:var(--fin-gold);color:#15161A;border-radius:999px;padding:10px 18px;font-weight:700;font-size:14px;transition:transform .15s,filter .2s}
.fin-root .fin-btn:hover{filter:brightness(1.06)} .fin-root .fin-btn:active{transform:scale(.98)} .fin-root .fin-btn:disabled{opacity:.5}
.fin-root .fin-btn-ghost{background:transparent;border:1px solid var(--fin-line);color:var(--fin-ink2);border-radius:999px;padding:7px 14px;font-weight:600;font-size:13px;transition:background-color .2s,border-color .2s,color .2s}
.fin-root .fin-btn-ghost:hover{background:rgba(255,255,255,.05);border-color:rgba(255,255,255,.18);color:var(--fin-ink)}
.fin-root .fin-table{border-collapse:separate;border-spacing:0;font-size:14px;width:100%}
.fin-root .fin-table th{font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--fin-muted);font-weight:600;text-align:left;padding:10px 12px;border-bottom:1px solid var(--fin-line)}
.fin-root .fin-table td{padding:12px;border-bottom:1px solid rgba(255,255,255,.05);vertical-align:middle} .fin-root .fin-table tr:last-child td{border-bottom:0}
.fin-root .fin-table th.num,.fin-root .fin-table td.num{text-align:right;font-variant-numeric:tabular-nums}
.fin-root .fin-nav a{display:inline-flex;align-items:center;gap:8px;padding:9px 16px;border-radius:999px;font-size:14px;font-weight:600;color:var(--fin-ink2);white-space:nowrap;transition:background-color .2s,color .2s}
.fin-root .fin-nav a:hover{color:var(--fin-ink);background:rgba(255,255,255,.05)} .fin-root .fin-nav a.active{background:var(--fin-gold-soft);color:var(--fin-warm)}
.fin-root .fin-nav::-webkit-scrollbar{display:none}
.fin-root details>summary{list-style:none;cursor:pointer} .fin-root details>summary::-webkit-details-marker{display:none}
.fin-root ::selection{background:var(--fin-gold-soft)}
.fin-root .fin-rise{animation:finRise .5s cubic-bezier(.2,.7,.2,1) both}
@keyframes finRise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
`;

export default function Finance() {
  const { business } = useAuth();
  const location = useLocation();
  const [tick, setTick] = useState(0);
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  if (business && business.finance_centre_enabled === false) return <Navigate to="/calendar" replace />;
  const refresh = () => setTick((t) => t + 1);
  const section = NAV.find((n) => (n.to === "" ? location.pathname === "/finance" : location.pathname.startsWith(`/finance/${n.to}`)));
  const href = (n) => (n.to === "" ? "/finance" : `/finance/${n.to}`);
  return (
    <div style={THEME} className="fin-root min-h-screen pb-10" data-testid="finance-root">
      <style>{CSS}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 sm:pt-7">
        <header className="flex items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="min-w-0">
            <div className="fin-label truncate">{business?.name}</div>
            <h1 className="fin-h text-2xl sm:text-3xl font-bold mt-0.5 truncate" data-testid="finance-title">{!section || section.to === "" ? "Finance" : section.label}</h1>
          </div>
          <nav className="fin-nav hidden md:flex gap-1 p-1 rounded-full shrink-0" style={{ background: "var(--fin-bg2)", border: "1px solid var(--fin-line)" }} data-testid="finance-nav">
            {NAV.map((n) => <NavLink key={n.to} to={href(n)} end={n.end} data-testid={`fin-nav-${n.to || "home"}`}><n.icon className="h-4 w-4" />{n.label}</NavLink>)}
          </nav>
        </header>
        <nav className="fin-nav md:hidden flex gap-1 overflow-x-auto -mx-4 px-4 pb-3 mb-4" data-testid="finance-tabs">
          {NAV.map((n) => <NavLink key={n.to} to={href(n)} end={n.end} data-testid={`fin-tab-${n.to || "home"}`}><n.icon className="h-4 w-4" />{n.label}</NavLink>)}
        </nav>
        <Routes>
          <Route index element={<FinOverview tick={tick} onChange={refresh} />} />
          <Route path="bills" element={<FinBills tick={tick} onChange={refresh} />} />
          <Route path="expenses" element={<FinExpenses tick={tick} onChange={refresh} />} />
          <Route path="tax" element={<div className="max-w-5xl"><FinTax onChange={refresh} /></div>} />
          <Route path="settings" element={<FinSettings tick={tick} onChange={refresh} />} />
          <Route path="out" element={<Navigate to="/finance/bills" replace />} />
          <Route path="*" element={<Navigate to="/finance" replace />} />
        </Routes>
      </div>
    </div>
  );
}
